import {
  type BaseQueryFn,
  createApi,
  type FetchArgs,
  fetchBaseQuery,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'

import { customerHolder } from '~/shell/api/rtk/customer'
import { sec } from '~/shell/api/rtk/security'
import { isReauthRequiredError } from '~/shell/auth/reauthRequiredError'
import { getApiBaseUrl, shouldBypassAuthForE2E } from '~/shell/config'

/**
 * Serialise query params so array values become repeated keys (`?status=Active&status=Discharged`) rather
 * than the comma-joined single value (`?status=Active,Discharged`) that `fetchBaseQuery`'s default
 * `URLSearchParams` pass would produce. FastAPI list-typed query params expect repeated keys, so this is what
 * a multi-value filter requires on the wire. `null`/`undefined` values are dropped, matching the default's
 * `stripUndefined` behaviour.
 */
const serializeParams = (params: Record<string, unknown>): string => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null) {
          search.append(key, String(item))
        }
      }
    } else {
      search.append(key, String(value))
    }
  }
  return search.toString()
}

/**
 * Dynamic base query that resolves the API base URL per-request.
 * This ensures the runtime config is loaded before the base URL is accessed.
 */
const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const rawBaseQuery = fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    paramsSerializer: serializeParams,
    prepareHeaders: async (headers) => {
      // Under the env-gated E2E bypass there is no Auth0 session, so skip the
      // token fetch entirely; the Playwright smoke mocks the API and does not
      // assert on the bearer header. Inert in normal dev and production.
      if (shouldBypassAuthForE2E) {
        return headers
      }
      try {
        const token = await sec.getAccessTokenSilently()
        if (token) {
          headers.set('authorization', `Bearer ${token}`)
        }
      } catch (error) {
        // A re-auth-required error means the cached session is expired or
        // rotated-out: kick off interactive login rather than firing a doomed
        // header-less request that would only 401 and strand the app on its
        // loader. Any other failure (unauthenticated route, transient
        // network/timeout) falls through and the request is sent without a bearer
        // token; a backend 401 then surfaces through the 401 middleware.
        if (isReauthRequiredError(error)) {
          sec.requestReauth()
        }
      }
      return headers
    },
    credentials: 'include',
  })

  // Backstop the active customer as a query param for requests made from inside the
  // customer route tree (SHELL-14). The generated endpoints take `customerRegardId` as a
  // real argument and every call site supplies it from the route, so this normally changes
  // nothing; it covers a query issued from a component that has no `:customer` param of its
  // own to read. The base query runs outside React and cannot read route params itself, so
  // it takes the customer from the holder bridge, which `CareGapsLayout` syncs from
  // `/c/:customer/`.
  //
  // Append-if-set rather than unconditionally: the holder is null outside a customer-scoped
  // route, and a request made there is deliberately unscoped — `Bootstrap` asks for the
  // accessible customer list in order to decide which customer to use, so naming one here
  // would presume the answer. No endpoint special-cases it; the param is simply omitted.
  //
  // `args` may be a bare URL string or a `FetchArgs` object, so it is normalised to
  // object form first. The caller's params are spread last: an explicit argument read
  // straight from the route wins over the holder, which an effect populates a tick later.
  const customerRegardId = customerHolder.get()
  if (customerRegardId === null) {
    return rawBaseQuery(args, api, extraOptions)
  }

  const fetchArgs: FetchArgs = typeof args === 'string' ? { url: args } : args
  return rawBaseQuery(
    {
      ...fetchArgs,
      params: {
        customer_regard_id: customerRegardId,
        ...fetchArgs.params,
      },
    },
    api,
    extraOptions,
  )
}

/**
 * The one RTK Query api instance, holding the base query and no endpoints of its own.
 *
 * Every generated client injects its endpoints here rather than creating an api of its own, so a client is a
 * set of endpoints and the cache, the tag invalidation, and the re-auth latch stay shared however many
 * clients there are. `reducerPath` is named for what it is rather than for a product, since there is only
 * ever one.
 */
export const baseApi = createApi({
  baseQuery: dynamicBaseQuery,
  endpoints: () => ({}),
  reducerPath: 'api',
})
